import { useEffect, useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Button, Stack, Typography } from '@mui/material';
import { LockOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSubscriptionFreeze } from 'features/subscription-freeze/SubscriptionFreezeContext';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useQuickSubscribe } from './useQuickSubscribe';
import { analytics } from 'lib/analytics';
import { inkButtonSx } from './inkButtonSx';

const DISPLAY_FONT = "'Instrument Sans', 'Inter', sans-serif";

/**
 * Feature name per plan-gated route prefix. First match wins (checked in
 * order), so longer prefixes go first. These pages are reachable by anyone —
 * the sidebar no longer intercepts locked clicks — and this banner is where
 * the subscribe ask now lives: on the page the user actually wanted.
 */
const GATED_ROUTES: Array<{ prefix: string; feature: string; paused: string; fresh: string }> = [
  // `feature` feeds analytics; `paused`/`fresh` are the user-facing headlines —
  // product statements ("Your copier is paused"), not menu labels.
  {
    prefix: '/trade-copier/copier-settings',
    feature: 'Copier Engine',
    paused: 'Your copier is paused',
    fresh: 'The copier is part of every plan'
  },
  {
    prefix: '/trade-copier/templates',
    feature: 'Copier Templates',
    paused: 'Your copier is paused',
    fresh: 'The copier is part of every plan'
  },
  {
    prefix: '/trade-copier/copier-logs',
    feature: 'Copier Logs',
    paused: 'Your copier is paused',
    fresh: 'The copier is part of every plan'
  },
  {
    prefix: '/account-trades',
    feature: 'Account Trades',
    paused: 'Your accounts are disconnected',
    fresh: 'Account trades come with every plan'
  }
  // Trading tools (market news, calendar, sentiments, integrations) are open
  // to everyone — no gate, no banner.
];

/**
 * Slim banner above gated page content while the viewer has no plan.
 * Analyzer pages are NOT in the map — they carry their own LockedContentOverlay.
 */
const PlanRequiredBanner = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isPaid, hadPreviousSub, hasPastDue } = useSubscriptionStatus();
  // A paused plan has its own banner, which offers the resume instead of a new plan.
  const { freeze } = useSubscriptionFreeze();

  const gate = useMemo(() => GATED_ROUTES.find((g) => pathname.startsWith(g.prefix)), [pathname]);
  const show = Boolean(gate) && isPaid === false && !freeze;

  useEffect(() => {
    if (show && gate) analytics.planBannerViewed({ feature: gate.feature, returning: hadPreviousSub });
  }, [show, gate, hadPreviousSub]);

  if (!show || !gate) return null;

  const dark = theme.palette.mode === 'dark';
  const teal = theme.palette.primary.main;

  return (
    <Box
      sx={{
        mb: 2.5,
        px: 2.5,
        py: 2,
        borderRadius: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        border: `1px solid ${alpha(teal, 0.35)}`,
        background: dark ? alpha(teal, 0.1) : alpha(teal, 0.06)
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: teal,
            bgcolor: alpha(teal, dark ? 0.15 : 0.1),
            flexShrink: 0
          }}
        >
          <LockOutlined />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.3 }}>
            {hasPastDue ? `${gate.paused} — payment needs attention` : hadPreviousSub ? gate.paused : gate.fresh}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasPastDue
              ? 'Your last payment failed. Settle it on the billing page and everything switches back on.'
              : hadPreviousSub
              ? 'Resubscribe and it switches back on with your old setup.'
              : 'Included with every subscription.'}
          </Typography>
        </Box>
      </Stack>
      <Button
        variant="contained"
        onClick={() => {
          analytics.getStartedCtaClicked({
            step: 'subscribe',
            source: 'plan_banner',
            feature: gate.feature,
            returning: hadPreviousSub,
            past_due: hasPastDue
          });
          // Overdue sub: renewal lives on the billing page, not in a new cart.
          if (hasPastDue) navigate('/account-settings/billing');
          else useQuickSubscribe.getState().openQuickSubscribe('cfd');
        }}
        sx={{
          fontFamily: DISPLAY_FONT,
          px: 2.5,
          ...inkButtonSx(dark)
        }}
      >
        {hasPastDue ? 'Fix payment' : hadPreviousSub ? 'Resubscribe' : 'Start your plan'}
      </Button>
    </Box>
  );
};

export default PlanRequiredBanner;
